/**
 * Validates if a string is a valid image URL
 * @param url - URL string to validate
 * @returns boolean indicating if URL is valid and points to an image
 */
export const validateImageUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;

  // Check if it's a valid URL format
  try {
    new URL(url);
  } catch (_) {
    return false;
  }

  // Check for common image extensions
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff'];
  const hasImageExtension = imageExtensions.some(ext => url.toLowerCase().endsWith(ext));

  // Check for common image path indicators
  const imagePathIndicators = [
    '/images/', '/img/', '/photos/', '/assets/images',
    '/thumbnails/', '/picture/', 'image', 'photo',
    '/assets/img', 'placeholder', 'cdn', '/uploads/'
  ];
  const containsImageIndicator = imagePathIndicators.some(indicator =>
    url.toLowerCase().includes(indicator.toLowerCase())
  );

  // Check for common image hosting domains
  const imageHostingDomains = [
    'imgur.com', 'flickr.com', 'unsplash.com', 'pexels.com',
    'cloudinary.com', 'imgbb.com', 'postimg.cc', 'imageshack.com',
    'dropbox.com/s', 'drive.google.com', 'ibb.co'
  ];
  const isFromImageHost = imageHostingDomains.some(domain =>
    url.toLowerCase().includes(domain.toLowerCase())
  );

  // Consider it an image if it has an image extension
  // OR contains image path indicators
  // OR comes from known image hosting
  return hasImageExtension || containsImageIndicator || isFromImageHost;
};

/**
 * Extract image URL from various formats
 * This handles special cases like Google Drive, Dropbox etc.
 * @param url - URL string to normalize
 * @returns Normalized direct image URL
 */
export const normalizeImageUrl = (url: string): string => {
  try {
    // Handle Google Drive URLs
    if (url.includes('drive.google.com/file/d/')) {
      const fileIdMatch = url.match(/\/file\/d\/([^/]+)/);
      if (fileIdMatch && fileIdMatch[1]) {
        return `https://drive.google.com/uc?export=view&id=${fileIdMatch[1]}`;
      }
    }

    // Handle Dropbox URLs
    if (url.includes('dropbox.com')) {
      return url.replace('www.dropbox.com', 'dl.dropboxusercontent.com')
                .replace('?dl=0', '')
                .replace('?dl=1', '');
    }

    // Handle Imgur URLs without direct image extension
    if (url.includes('imgur.com') && !url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      if (url.includes('/a/') || url.includes('/gallery/')) {
        // These are album links, can't convert to direct image
        return url;
      }

      // Extract the image ID and convert to direct image link
      const imgurMatch = url.match(/imgur\.com\/([^/.]+)$/);
      if (imgurMatch && imgurMatch[1]) {
        return `https://i.imgur.com/${imgurMatch[1]}.jpg`;
      }
    }

    return url;
  } catch (e) {
    // If any processing fails, return original URL
    return url;
  }
};

// Create a named object before exporting as default
const imageUrlValidators = {
  validateImageUrl,
  normalizeImageUrl
};

export default imageUrlValidators;